/**
 * WhatsApp number checks per country (PHP-4052). Mirrors `backend/app/modules/workspace/phone.py`:
 * the number must have a possible length for the selected country (libphonenumber data). Only the
 * length is enforced, not whether the prefix is allocated, so a real number in a newly opened
 * range is never refused.
 */
import {
  getExampleNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js/min";
import type { CountryCode } from "libphonenumber-js/min";
import examples from "libphonenumber-js/mobile/examples";

/** The name used in messages ("Enter a valid UK number"), per ISO code in `COUNTRIES`. */
const NUMBER_NAMES: Record<string, string> = {
  GB: "UK",
  IE: "Irish",
  US: "US",
  CA: "Canadian",
  AU: "Australian",
  NZ: "New Zealand",
  FR: "French",
  DE: "German",
  ES: "Spanish",
  IT: "Italian",
  NL: "Dutch",
  PT: "Portuguese",
  GR: "Greek",
  PL: "Polish",
  AE: "UAE",
  SA: "Saudi",
  IN: "Indian",
  BD: "Bangladeshi",
  PK: "Pakistani",
  SG: "Singapore",
  MY: "Malaysian",
  HK: "Hong Kong",
  JP: "Japanese",
  ZA: "South African",
  NG: "Nigerian",
  KE: "Kenyan",
  BR: "Brazilian",
  MX: "Mexican",
};

// The runtime refuses a wa.me path outside 8-15 digits (dial code included).
const MIN_E164_DIGITS = 8;
const MAX_E164_DIGITS = 15;

/** "7700900123" → "7700 900123", the grouping in the design (4 + rest). */
export function grouped(digits: string): string {
  return digits.length > 6 ? `${digits.slice(0, 4)} ${digits.slice(4)}` : digits;
}

/** "UK numbers usually have 10 digits after +44, e.g. 7400 123456." */
export function lengthHint(country: string, dial: string): string {
  const name = NUMBER_NAMES[country] ?? country;
  const example = getExampleNumber(country as CountryCode, examples)?.nationalNumber;
  return example
    ? `${name} numbers usually have ${example.length} digits after +${dial}, e.g. ${grouped(example)}.`
    : `Check the number after +${dial}.`;
}

export type NumberCheck =
  /** Nothing typed yet. */
  | { state: "empty" }
  /** `national`: digits only, no trunk 0 (Italy keeps its 0), as the API stores it. */
  | { state: "valid"; national: string }
  | { state: "invalid"; error: string };

/** Accepts "07700 900123", "7700 900123" or "+44 7700 900123". */
export function checkNumber(country: string, dial: string, number: string): NumberCheck {
  if (!/\d/.test(number)) return { state: "empty" };
  const name = NUMBER_NAMES[country] ?? country;
  const invalid: NumberCheck = {
    state: "invalid",
    error: `Enter a valid ${name} number. ${lengthHint(country, dial)}`,
  };
  const parsed = parsePhoneNumberFromString(number, country as CountryCode);
  if (!parsed) return invalid;
  if (parsed.countryCallingCode !== dial)
    return {
      state: "invalid",
      error: `This number starts with +${parsed.countryCallingCode}, not +${dial}. Choose its country, or enter the number without the country code.`,
    };
  if (validatePhoneNumberLength(number, country as CountryCode) !== undefined) return invalid;
  const national = parsed.nationalNumber;
  const total = dial.length + national.length;
  if (total < MIN_E164_DIGITS || total > MAX_E164_DIGITS) return invalid;
  return { state: "valid", national };
}
